import { msalInstance, apiScopes } from '../auth/msal';
import { signedInAccount } from '../auth/ssoMemory';
import type { AmbiguityCandidate, FacilityDetail, PortfolioResponse, TeamClockResponse } from './types';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: string,
    readonly candidates?: AmbiguityCandidate[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function token(): Promise<string> {
  // The ACTIVE account — the link's person, or whoever just signed in (auth/ssoMemory.ts).
  const account = signedInAccount(msalInstance, msalInstance.getAllAccounts());
  if (!account) throw new ApiError(401, 'Not signed in');
  const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
  return result.accessToken;
}

async function request<T>(path: string): Promise<T> {
  const accessToken = await token();
  const res = await fetch(`${API_BASE}/api${path}`, {
    headers: { authorization: `Bearer ${accessToken}`, accept: 'application/json' },
  });

  if (!res.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      res.status,
      String(body.message ?? body.error ?? `Request failed (${res.status})`),
      typeof body.detail === 'string' ? body.detail : undefined,
      Array.isArray(body.candidates) ? (body.candidates as AmbiguityCandidate[]) : undefined,
    );
  }

  return (await res.json()) as T;
}

export const api = {
  portfolio: () => request<PortfolioResponse>('/facilities'),
  facility: (id: string) => request<FacilityDetail>(`/facilities/${encodeURIComponent(id)}`),
  /** PunchWISE clock status for the On-site team. Never part of `facility()`. */
  teamClock: (id: string) => request<TeamClockResponse>(`/facilities/${encodeURIComponent(id)}/team-clock`),
};
